import React, { useState, useEffect } from 'react';
import Button from '../../../../components/Button';
import ModuleSection from '../ModuleSection';
import blogStorage from '../../utils/storage';
import './index.scss';

const TagManager = () => {
  const [tags, setTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [editingTag, setEditingTag] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadTags();
  }, []);

  useEffect(() => {
    filterTags();
  }, [tags, searchQuery]);

  const loadTags = () => {
    const tagsData = blogStorage.getTags();
    // 更新标签的文章数量
    const articles = blogStorage.getArticles();
    const updatedTags = tagsData.map(tag => ({
      ...tag,
      articleCount: articles.filter(article => 
        article.tags.includes(tag.name)
      ).length
    }));
    setTags(updatedTags);
  };

  const filterTags = () => {
    let filtered = [...tags];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(tag =>
        tag.name.toLowerCase().includes(query) ||
        tag.description.toLowerCase().includes(query)
      );
    }
    
    // 按使用次数排序
    filtered.sort((a, b) => b.articleCount - a.articleCount);
    
    setFilteredTags(filtered);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreate = () => {
    setIsCreating(true);
    setEditingTag(null);
    setFormData({ name: '', description: '' });
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setIsCreating(false);
    setFormData({
      name: tag.name,
      description: tag.description
    });
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('请输入标签名称');
      return;
    }

    try {
      if (editingTag) {
        // 编辑模式
        const updated = blogStorage.updateTag(editingTag.id, formData);
        if (updated) {
          loadTags();
          handleCancel();
        }
      } else {
        // 创建模式
        const newTag = blogStorage.addTag(formData);
        if (newTag) {
          loadTags();
          handleCancel();
        }
      }
    } catch (error) {
      console.error('保存标签失败:', error);
      alert('保存失败，请重试');
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingTag(null);
    setFormData({ name: '', description: '' });
  };

  const handleDelete = (tag) => {
    if (tag.articleCount > 0) {
      if (!confirm(`标签"${tag.name}"被${tag.articleCount}篇文章使用，删除后将从这些文章中移除。确定要删除吗？`)) {
        return;
      }
    } else {
      if (!confirm(`确定要删除标签"${tag.name}"吗？`)) {
        return;
      }
    }

    try {
      const success = blogStorage.deleteTag(tag.id);
      if (success) {
        loadTags();
      }
    } catch (error) {
      console.error('删除标签失败:', error);
      alert('删除失败，请重试');
    }
  };

  return (
    <div className="tag-manager">
      {/* 创建/编辑表单 */}
      {(isCreating || editingTag) && (
        <ModuleSection
          title={editingTag ? '编辑标签' : '创建标签'}
          variant="info"
        >
          
          <div className="form-group">
            <label>标签名称 *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="请输入标签名称"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>标签描述</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="请输入标签描述（可选）"
              className="form-textarea"
              rows={3}
            />
          </div>

          <div className="form-actions">
            <Button 
              onClick={handleCancel}
            >
              取消
            </Button>
            <Button 
              type="primary"
              onClick={handleSave}
            >
              保存
            </Button>
          </div>
        </ModuleSection>
      )}

      {/* 标签列表 */}
      <ModuleSection
        title="标签管理"
        headerExtra={`共 ${filteredTags.length} 个标签`}
        actions={
          <div className="tag-header-actions">
            <div className="search-input-wrapper">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="搜索标签..."
                className="search-input"
              />
            </div>
            <Button 
              type="primary"
              onClick={handleCreate}
            >
              + 创建标签
            </Button>
          </div>
        }
        variant="list"
        noPadding
      >
        <div className="tags-list">
        {filteredTags.length > 0 ? (
          filteredTags.map(tag => (
            <div key={tag.id} className="tag-item">
              <div className="tag-info">
                <div className="tag-main">
                  <h4>{tag.name}</h4>
                  <p className="tag-desc">{tag.description || '暂无描述'}</p>
                </div>
                <div className="tag-stats">
                  <span className="article-count">{tag.articleCount} 篇文章</span>
                  <span className="create-time">
                    创建于 {new Date(tag.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              <div className="tag-actions">
                <Button 
                  size="small"
                  onClick={() => handleEdit(tag)}
                >
                  编辑
                </Button>
                <Button 
                  size="small"
                  type="danger"
                  onClick={() => handleDelete(tag)}
                >
                  删除
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🏷️</div>
            <p>暂无标签，点击"创建标签"开始吧！</p>
          </div>
        )}
        </div>
      </ModuleSection>
    </div>
  );
};

export default TagManager;
